import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

const MERCADO_PAGO_SDK_URL = "https://sdk.mercadopago.com/js/v2";
let sdkPromise = null;

function loadMercadoPagoSdk() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Mercado Pago solo puede iniciarse desde el navegador."),
    );
  }

  if (window.MercadoPago) return Promise.resolve(window.MercadoPago);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (!window.MercadoPago) {
        reject(new Error("MercadoPago.js no quedó disponible."));
        return;
      }
      resolve(window.MercadoPago);
    };

    const fail = () => {
      reject(new Error("No se pudo cargar el SDK seguro de Mercado Pago."));
    };

    const existingScript = document.querySelector(
      `script[src="${MERCADO_PAGO_SDK_URL}"]`,
    );

    if (existingScript) {
      existingScript.addEventListener("load", finish, { once: true });
      existingScript.addEventListener("error", fail, { once: true });

      window.setTimeout(() => {
        if (window.MercadoPago) finish();
      }, 0);
      return;
    }

    const script = document.createElement("script");
    script.src = MERCADO_PAGO_SDK_URL;
    script.async = true;
    script.dataset.webprofitMercadoPagoSdk = "true";
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", fail, { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
}

const baseControlSx = {
  width: "100%",
  height: 42,
  minHeight: 42,
  maxHeight: 42,
  boxSizing: "border-box",
  border: "1px solid rgba(13,31,45,.18)",
  borderRadius: 1.5,
  bgcolor: "#fff",
  px: 1.25,
  font: "inherit",
  fontSize: ".92rem",
  lineHeight: 1.2,
  color: "#0D1F2D",
  outline: "none",
  transition: "border-color .2s ease, box-shadow .2s ease",
  overflow: "hidden",
  "&:focus": {
    borderColor: "primary.main",
    boxShadow: "0 0 0 3px rgba(230,67,36,.10)",
  },
};

const iframeControlSx = {
  ...baseControlSx,
  px: 0,
  display: "block",
  "& iframe": {
    width: "100% !important",
    height: "42px !important",
    minHeight: "42px !important",
    maxHeight: "42px !important",
    border: "0 !important",
    display: "block !important",
    margin: "0 !important",
    padding: "0 !important",
  },
};

function Field({ label, children, helper }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="label"
        sx={{
          display: "block",
          mb: 0.45,
          fontSize: ".78rem",
          lineHeight: 1.2,
          fontWeight: 850,
          color: "text.primary",
        }}
      >
        {label}
      </Typography>
      {children}
      {helper ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.45, display: "block", lineHeight: 1.25 }}
        >
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function MercadoPagoCardForm({
  publicKey,
  amount,
  locale = "es-AR",
  currency = "ARS",
  buyer,
  processing = false,
  onSubmit,
  onReady,
  onError,
}) {
  const instanceId = useRef(
    `mp-card-${Math.random().toString(36).slice(2, 10)}`,
  );
  const cardFormRef = useRef(null);
  const submitRef = useRef(onSubmit);
  const readyRef = useRef(onReady);
  const errorRef = useRef(onError);
  const processingRef = useRef(processing);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    submitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    readyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    errorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  const ids = useMemo(() => {
    const prefix = instanceId.current;
    return {
      form: `${prefix}-form`,
      cardNumber: `${prefix}-card-number`,
      expirationDate: `${prefix}-expiration-date`,
      securityCode: `${prefix}-security-code`,
      cardholderName: `${prefix}-cardholder-name`,
      issuer: `${prefix}-issuer`,
      installments: `${prefix}-installments`,
      identificationType: `${prefix}-identification-type`,
      identificationNumber: `${prefix}-identification-number`,
      email: `${prefix}-email`,
      submit: `${prefix}-submit`,
    };
  }, []);

  useEffect(() => {
    let active = true;
    let mountedForm = null;

    async function mount() {
      if (!publicKey || !(Number(amount) > 0)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLocalError("");

      try {
        const MercadoPago = await loadMercadoPagoSdk();
        if (!active) return;

        const mp = new MercadoPago(publicKey, { locale });

        mountedForm = mp.cardForm({
          amount: Number(amount).toFixed(2),
          iframe: true,
          form: {
            id: ids.form,
            cardNumber: {
              id: ids.cardNumber,
              placeholder: "Número de tarjeta",
            },
            expirationDate: { id: ids.expirationDate, placeholder: "MM/AA" },
            securityCode: { id: ids.securityCode, placeholder: "Código" },
            cardholderName: {
              id: ids.cardholderName,
              placeholder: "Como figura en la tarjeta",
            },
            issuer: { id: ids.issuer, placeholder: "Banco emisor" },
            installments: { id: ids.installments, placeholder: "Cuotas" },
            identificationType: { id: ids.identificationType },
            identificationNumber: {
              id: ids.identificationNumber,
              placeholder: "Número de documento",
            },
            cardholderEmail: {
              id: ids.email,
              placeholder: "Correo electrónico",
            },
          },
          callbacks: {
            onFormMounted: (error) => {
              if (!active) return;
              if (error) {
                const nextError = new Error(
                  "No se pudo iniciar el formulario de tarjeta.",
                );
                setLocalError(nextError.message);
                setLoading(false);
                errorRef.current?.(nextError);
                return;
              }
              cardFormRef.current = mountedForm;
              setLoading(false);
              readyRef.current?.();
            },
            onSubmit: async (event) => {
              event.preventDefault();
              if (processingRef.current) return;
              setLocalError("");

              try {
                const data = mountedForm.getCardFormData();
                await submitRef.current?.({
                  formData: {
                    token: data.token,
                    payment_method_id: data.paymentMethodId,
                    issuer_id: data.issuerId,
                    installments: Number(data.installments || 1),
                    payer: {
                      email: data.cardholderEmail,
                      identification: {
                        type: data.identificationType,
                        number: data.identificationNumber,
                      },
                    },
                  },
                });
              } catch (error) {
                const message =
                  error?.message || "No se pudo procesar la tarjeta.";
                setLocalError(message);
                errorRef.current?.(error);
              }
            },
            onFetching: () => {
              if (!active) return undefined;
              setFetching(true);
              return () => {
                if (active) setFetching(false);
              };
            },
          },
        });
      } catch (error) {
        if (!active) return;
        const message = error?.message || "No se pudo cargar Mercado Pago.";
        setLocalError(message);
        setLoading(false);
        errorRef.current?.(error);
      }
    }

    mount();

    return () => {
      active = false;
      cardFormRef.current = null;
      if (mountedForm && typeof mountedForm.unmount === "function") {
        try {
          mountedForm.unmount();
        } catch {
          // Algunas versiones de MercadoPago.js no exponen cleanup completo.
        }
      }
    };
  }, [amount, ids, locale, publicKey]);

  const busy = loading || fetching || processing;

  return (
    <Box sx={{ width: "100%", maxWidth: 620 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, mb: 0.45 }}>
        <CreditCardRoundedIcon color="primary" sx={{ fontSize: 22 }} />
        <Typography sx={{ fontSize: "1rem", fontWeight: 950 }}>
          Tarjeta de crédito o débito
        </Typography>
      </Box>

      <Typography
        color="text.secondary"
        sx={{ mb: 1.75, fontSize: ".84rem", lineHeight: 1.45 }}
      >
        Los datos sensibles se tokenizan directamente con Mercado Pago y nunca
        se guardan en WebProfit.
      </Typography>

      {localError ? (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25 }}>
          {localError}
        </Alert>
      ) : null}

      <Box
        component="form"
        id={ids.form}
        noValidate
        sx={{ position: "relative" }}
      >
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              bgcolor: "rgba(255,255,255,.9)",
              display: "grid",
              placeItems: "center",
              borderRadius: 1.5,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={24} />
              <Typography
                color="text.secondary"
                sx={{ mt: 0.75, fontSize: ".82rem" }}
              >
                Preparando el pago seguro…
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Grid container spacing={1.35}>
          <Grid item xs={12}>
            <Field label="Número de tarjeta">
              <Box id={ids.cardNumber} sx={iframeControlSx} />
            </Field>
          </Grid>

          <Grid item xs={7} sm={8}>
            <Field label="Vencimiento">
              <Box id={ids.expirationDate} sx={iframeControlSx} />
            </Field>
          </Grid>

          <Grid item xs={5} sm={4}>
            <Field label="Código de seguridad">
              <Box id={ids.securityCode} sx={iframeControlSx} />
            </Field>
          </Grid>

          <Grid item xs={12}>
            <Field label="Titular de la tarjeta">
              <Box
                component="input"
                id={ids.cardholderName}
                defaultValue={buyer?.fullName || ""}
                autoComplete="cc-name"
                sx={baseControlSx}
              />
            </Field>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field label="Banco emisor">
              <Box component="select" id={ids.issuer} sx={baseControlSx} />
            </Field>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field label="Cuotas">
              <Box component="select" id={ids.installments} sx={baseControlSx} />
            </Field>
          </Grid>

          <Grid item xs={4} sm={3}>
            <Field label="Documento">
              <Box
                component="select"
                id={ids.identificationType}
                sx={baseControlSx}
              />
            </Field>
          </Grid>

          <Grid item xs={8} sm={9}>
            <Field label="Número">
              <Box
                component="input"
                id={ids.identificationNumber}
                defaultValue={buyer?.dni || ""}
                inputMode="numeric"
                sx={baseControlSx}
              />
            </Field>
          </Grid>

          <Grid item xs={12}>
            <Field label="Correo del pagador">
              <Box
                component="input"
                id={ids.email}
                type="email"
                defaultValue={buyer?.email || ""}
                autoComplete="email"
                sx={baseControlSx}
              />
            </Field>
          </Grid>
        </Grid>

        <Button
          id={ids.submit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={busy}
          startIcon={
            processing ? (
              <CircularProgress size={17} color="inherit" />
            ) : (
              <LockRoundedIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            mt: 1.75,
            minHeight: 42,
            py: 0.85,
            borderRadius: 1.5,
            fontSize: ".9rem",
            fontWeight: 950,
            textTransform: "none",
          }}
        >
          {processing
            ? "Procesando pago…"
            : `Pagar ${Number(amount).toLocaleString(locale, {
                style: "currency",
                currency,
              })}`}
        </Button>
      </Box>
    </Box>
  );
}
